import React, { useState, useEffect, useRef } from 'react';
import { BrainCircuit, ChevronDown, Search, FileText, Loader2, CheckCircle2, Sparkles } from 'lucide-react';

interface Activity {
  action: string;   // 'search' | 'read' | 'think'
  label: string;    // e.g. "Écritures comptables", "Base de connaissances OHADA"
  detail: string;   // e.g. "5 écritures pertinentes trouvées"
  status: 'working' | 'done';
}

interface ThinkingBlockProps {
  content: string;
  isActive: boolean; // true = thinking in progress, false = done
  activities?: Activity[];
}

function ActivityIcon({ action, status }: { action: string; status: string }) {
  if (status === 'working') {
    return <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin text-cyan-500" />;
  }
  if (status === 'done') {
    return <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-emerald-500" />;
  }
  switch (action) {
    case 'search': return <Search className="w-3.5 h-3.5 flex-shrink-0" />;
    case 'read': return <FileText className="w-3.5 h-3.5 flex-shrink-0" />;
    case 'think': return <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />;
    default: return <FileText className="w-3.5 h-3.5 flex-shrink-0" />;
  }
}

export function ThinkingBlock({ content, isActive, activities = [] }: ThinkingBlockProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  // Auto-collapse when thinking finishes
  useEffect(() => {
    if (!isActive && (content || activities.length > 0)) {
      const timer = setTimeout(() => setIsExpanded(false), 600);
      return () => clearTimeout(timer);
    }
  }, [isActive, content, activities.length]);

  // Auto-scroll within the thinking block while streaming
  useEffect(() => {
    if (isActive && isExpanded && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, isActive, isExpanded]);

  if (!content && activities.length === 0) return null;

  const hasActivities = activities.length > 0;
  const workingCount = activities.filter(a => a.status === 'working').length;

  return (
    <div className="mb-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium
          transition-all duration-200 w-full text-left
          ${isActive
            ? 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800'
            : 'bg-muted/50 text-muted-foreground border border-border hover:bg-muted'
          }
        `}
      >
        <BrainCircuit className={`w-3.5 h-3.5 flex-shrink-0 ${isActive ? 'animate-pulse' : ''}`} />
        <span>
          {isActive
            ? workingCount > 0
              ? `Analyse en cours... (${activities.length} sources)`
              : 'Réflexion en cours...'
            : `Réflexion terminée${hasActivities ? ` — ${activities.length} sources consultées` : ''}`
          }
        </span>
        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {isExpanded && (
        <div
          ref={contentRef}
          className={`
            mt-1 rounded-lg text-xs leading-relaxed
            max-h-56 overflow-y-auto
            border border-border/50
            ${isActive
              ? 'bg-cyan-50/50 dark:bg-cyan-950/20'
              : 'bg-muted/30'
            }
          `}
        >
          {/* Activity items — files/resources being consulted */}
          {hasActivities && (
            <div className="px-3 py-2 space-y-1.5">
              {activities.map((activity, i) => (
                <div
                  key={`${activity.label}-${i}`}
                  className={`
                    flex items-center gap-2 py-1 px-2 rounded-md text-xs
                    ${activity.status === 'working'
                      ? 'bg-cyan-100/60 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                      : 'bg-emerald-50/60 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                    }
                  `}
                >
                  <ActivityIcon action={activity.action} status={activity.status} />
                  <span className="font-medium">{activity.label}</span>
                  <span className="text-[10px] opacity-70 ml-auto">{activity.detail}</span>
                </div>
              ))}
            </div>
          )}

          {/* Thinking content — model's reasoning text */}
          {content && (
            <div className={`px-3 py-2 ${hasActivities ? 'border-t border-border/30' : ''}`}>
              <pre className={`whitespace-pre-wrap font-sans ${isActive ? 'text-cyan-800 dark:text-cyan-200' : 'text-muted-foreground'}`}>
                {content}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
